import { useState, useCallback, useMemo } from 'react';
import html2canvas from 'html2canvas';
import { Download } from 'react-feather';
import styled from '@emotion/styled';
import { useFloating, useHover, useInteractions } from '@floating-ui/react';
import type { Chart, QuickChartData } from '@trinity/chat';
import { ReportFormat } from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useAsyncMutationDownloader } from '@trinity/chat-components/containers/QueryDownloader/index.js';
import { useOptionsColors } from '../../SurveyResponses/hooks/usePaletteColors';

type Props = {
  chartData: QuickChartData;
  title: string;
  imageExportRef?: React.RefObject<{ getImageExportRef: () => HTMLTableElement | null }>;
};

const useDownloadChartImage = (chartData: Chart, colors: string[]) => {
  const apiClient = useChatApiClient();

  return useAsyncMutationDownloader({
    mutationFn: () => {
      return apiClient.chatReports.downloadChartImage({
        chartData,
        title: chartData.chart.title || 'Chart',
        colors,
      });
    },
  });
};

const useDownloadChartPptx = (chartData: Chart, colors: string[]) => {
  const apiClient = useChatApiClient();

  return useAsyncMutationDownloader({
    mutationFn: () => {
      return apiClient.chatReports.downloadChartPptx({
        chartData,
        title: chartData.chart.title || 'Chart',
        colors,
      });
    },
  });
};

const useDownloadElementAsImage = (
  imageExportRef: React.RefObject<{ getImageExportRef: () => HTMLTableElement | null }>,
  title: string
) => {
  return useCallback(async () => {
    try {
      // Get image export element using ref instead of querySelector
      const imageExportElement = imageExportRef?.current?.getImageExportRef();

      if (!imageExportElement) {
        console.error('Image export element not found for download');
        return;
      }

      console.log('Downloading image export element:', imageExportElement);

      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'fixed';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.backgroundColor = 'transparent';
      tempContainer.style.zIndex = '-1';
      tempContainer.style.display = 'inline-block';
      tempContainer.style.padding = '0';

      const clonedContainer = imageExportElement.cloneNode(true) as HTMLElement;

      tempContainer.appendChild(clonedContainer);
      document.body.appendChild(tempContainer);

      // Get the actual content dimensions
      const contentWidth = clonedContainer.offsetWidth;
      const contentHeight = clonedContainer.offsetHeight;

      // Use html2canvas to capture the table
      const canvas = await html2canvas(tempContainer, {
        backgroundColor: 'transparent',
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: 0,
        width: contentWidth,
        height: contentHeight,
        onclone: (clonedDoc) => {
          // Ensure styles are properly applied in the cloned document
          const clonedTempContainer = clonedDoc.querySelector('[style*="position: fixed"]');
          if (clonedTempContainer instanceof HTMLElement) {
            clonedTempContainer.style.position = 'static';
            clonedTempContainer.style.left = '0';
          }
        },
      });

      // Clean up the temporary container
      document.body.removeChild(tempContainer);

      // Download the canvas as image
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${title.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_')}_table.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }, 'image/png');
    } catch (error) {
      console.error('Error downloading image export element as image:', error);
    }
  }, [imageExportRef, title]);
};

export function ChartDownload({ chartData, title, imageExportRef }: Props) {
  const [isOpen, setIsOpen] = useState(false);

  // Use the options colors hook to get chart colors
  const colors = useOptionsColors();

  // Construct a Chart type for the backend
  const exportChart: Chart = {
    type: ['data-table', 'kpi-widget'].includes(chartData.type) ? 'none' : (chartData.type as Chart['type']),
    chart: 'chart' in chartData ? (chartData.chart as Chart['chart']) : { title: '', xAxisTitle: '', yAxisTitle: '', data: [] },
    response: '',
  };

  const downloadPptxMutation = useDownloadChartPptx(exportChart, colors);
  const downloadImageMutation = useDownloadChartImage(exportChart, colors);
  const downloadElementAsImage = useDownloadElementAsImage(imageExportRef, title);

  const { mutate: downloadPptx } = downloadPptxMutation;
  const { mutate: downloadImage } = downloadImageMutation;

  const { context, refs, floatingStyles } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
  });

  const hover = useHover(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const handlePptxDownload = useCallback(() => {
    downloadPptx({
      meta: {
        format: ReportFormat.PowerPoint,
        name: title,
        title: 'PowerPoint Export',
      },
    });
  }, [downloadPptx, title]);

  const handleImageDownload = useCallback(() => {
    downloadImage({
      meta: {
        format: ReportFormat.Img,
        name: title,
        title: 'Image Export',
      },
    });
  }, [downloadImage, title]);

  return (
    <div ref={refs.setReference} {...getReferenceProps()}>
      <DownloadButton>
        <StyledIcon />
      </DownloadButton>

      {isOpen && (
        <PopperMenu ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
          {['data-table', 'kpi-widget'].includes(chartData.type) ? (
            <PopperMenuItem onClick={downloadElementAsImage}>Save as Image</PopperMenuItem>
          ) : (
            <>
              <PopperMenuItem onClick={handlePptxDownload}>Export as PPT</PopperMenuItem>
              <PopperMenuItem onClick={handleImageDownload}>Save as Image</PopperMenuItem>
            </>
          )}
        </PopperMenu>
      )}
    </div>
  );
}

const DownloadButton = styled.div`
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  color: #666;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #f0f0f0;
    color: #333;
  }
`;

const StyledIcon = styled(Download)`
  font-size: 12px !important;
  display: flex;
  flex-shrink: 0;
`;

const PopperMenu = styled.div`
  background-color: white;
  padding: 8px;
  border-radius: 4px;
  box-shadow: 0 2px 6px 2px rgba(60, 64, 67, 0.15);
  z-index: 1000;
  text-align: left;
  border: 1px solid #e0e0e0;
`;

const PopperMenuItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 2px;
  font-size: 14px;
  color: #333;

  &:hover {
    background-color: #f5f5f5;
  }
`;
